/**
 * «Popular now» на Buy-табе лендинга Andys (#1469): какие машины
 * показать и в каком виде. Чистые функции без window/config — контейнер
 * передаёт живой инвентарь (useCars) и адрес каталога дилера.
 */
import { isReservedCar } from '../../../utils/reservedCars';
import { getDisplayImage } from '../../../utils/carImage';

export const POPULAR_CARS_LIMIT = 10;

// Дефолтный порядок каталога — menuIndex по убыванию (ручной порядок
// оператора), машины без menuIndex — в конец. Повторяет case 'menu-index' в
// CarsPage.jsx: карусель обязана идти в том же порядке, что и витрина.
const compareCatalogDefault = (a, b) =>
  (b.menuIndex ?? Number.MIN_SAFE_INTEGER) - (a.menuIndex ?? Number.MIN_SAFE_INTEGER);

/**
 * Свободные (не reserved) hot offers в порядке каталога, не больше `limit`.
 * Свободных hot offers нет — первые `limit` свободных машин в том же порядке.
 *
 * @param {Array<object>} cars — инвентарь (DealerCarDto из fetchCarInventory).
 * @param {number} [limit]
 * @returns {Array<object>}
 */
export function selectPopularCars(cars, limit = POPULAR_CARS_LIMIT) {
  const available = (cars || []).filter((car) => !isReservedCar(car)).sort(compareCatalogDefault);
  const hot = available.filter((car) => car.isHotOffer);
  return (hot.length > 0 ? hot : available).slice(0, limit);
}

/**
 * Цена — тем же выражением, что на карточках каталога (CarCard, SalonCarCard):
 * `€${price.toLocaleString()}`, а 0 / нет цены → «Contact for price». Общего
 * хелпера у каталога нет — форматирование там инлайновое.
 */
export const formatPopularCarPrice = (price) =>
  typeof price === 'number' && price > 0 ? `€${price.toLocaleString()}` : 'Contact for price';

// Звёздочки в make/model — служебная пометка стока; каталог их тоже срезает.
const clean = (value) => (value || '').replace(/\*/g, '').trim();

/**
 * Слайд карусели: всё, что нужно презентационному компоненту, уже посчитано.
 * Ссылка — детальная машины на сайте дилера, тот же формат, что у mini-catalog
 * (`<catalogUrl>#/cars/<id>`, CarsPage.jsx).
 *
 * @param {object} car
 * @param {string} catalogUrl — DEALER_CATALOG_URL или прод-фолбэк.
 * `priceValue` — цена числом (EUR) для аналитики тапов (#1482); null там, где
 * плитка пишет «Contact for price».
 *
 * @returns {{id: string, title: string, year: number|null, price: string, priceValue: number|null, image: string|null, href: string}}
 */
export function toPopularCarSlide(car, catalogUrl) {
  return {
    id: car.id,
    title: [clean(car.make), clean(car.model)].filter(Boolean).join(' '),
    year: car.year ?? null,
    price: formatPopularCarPrice(car.price),
    priceValue: typeof car.price === 'number' && car.price > 0 ? car.price : null,
    image: getDisplayImage(car),
    href: `${catalogUrl.replace(/\/+$/, '')}#/cars/${encodeURIComponent(car.id)}`,
  };
}

/** selectPopularCars + toPopularCarSlide — то, что контейнер отдаёт пропом. */
export const buildPopularCarSlides = (cars, catalogUrl, limit = POPULAR_CARS_LIMIT) =>
  selectPopularCars(cars, limit).map((car) => toPopularCarSlide(car, catalogUrl));
